import { IOrganization, Organization } from "@app/shared/src/models";

/**
 * The organization form's working copy, shared by every place a director can create one: the
 * welcome page a user with no organization lands on, the Organizations tab of "Your account",
 * and the "+ Add new organization" row at the foot of the topbar picker.
 *
 * Plain strings rather than the generated model so the inputs stay controlled and blank fields
 * only become `undefined` at the moment the model is built for the server.
 */
export type OrganizationDraft = {
    name: string;
    phoneNumber: string;
    emailAddress: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zipcode: string;
};

export const emptyOrganizationDraft = (): OrganizationDraft => ({
    name: "",
    phoneNumber: "",
    emailAddress: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
});

/** Loose on purpose: it stops "not an email" from reaching the server, nothing stricter. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The first thing wrong with the draft, in the words the form shows — or nothing when it is fine. */
export function organizationDraftError(draft: OrganizationDraft): string | undefined {
    if (!draft.name.trim()) return "Give the organization a name.";
    const email = draft.emailAddress.trim();
    if (email && !EMAIL_SHAPE.test(email)) return "That doesn't look like an email address.";
    return undefined;
}

const blankToUndefined = (value: string) => {
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
};

/** The model the save endpoint takes. No id: the server assigns one and makes the caller its administrator. */
export function toOrganization(draft: OrganizationDraft): Organization {
    return new Organization({
        id: undefined,
        isDeleted: false,
        name: draft.name.trim(),
        phoneNumber: blankToUndefined(draft.phoneNumber),
        emailAddress: blankToUndefined(draft.emailAddress),
        address: blankToUndefined(draft.address),
        address2: blankToUndefined(draft.address2),
        city: blankToUndefined(draft.city),
        state: blankToUndefined(draft.state),
        zipcode: blankToUndefined(draft.zipcode),
    } as IOrganization);
}
